/**
 * B1021 (c) — gift cards as a TENDER on the Pay step: the pure half.
 *
 * The number is judged here BEFORE the network (16 digits, Luhn — the same
 * check digit the mint appends, B1019), the PIN is four digits, and the sums
 * say what the applied cards pay and what is left for the bank card. The
 * storefront does not depend on @transact/domain, so the Luhn check lives here
 * too (the domain's `luhnValid` is the reference; the pure tests pin both to
 * the same numbers).
 */
import { formatMoney, type Money } from "./money.js";

/** One card applied to the pending order — giftcard.redeem's answer, kept. */
export interface AppliedGiftCard {
  readonly intentId: string;
  readonly lastFour: string;
  /** What this card pays on this order. */
  readonly amount: Money;
  /** What stays on the card once the order confirms. */
  readonly left: Money;
}

/** The lock rule in one sentence — never a per-card count of tries (B1022's enumeration default). */
export const GIFT_CARD_LOCK_NOTE = "Three wrong PINs lock a card for an hour.";

const digitsOf = (raw: string): string => raw.replace(/[\s-]/g, "");

function luhnValid(digits: string): boolean {
  let sum = 0;
  for (let i = 0; i < digits.length; i += 1) {
    let d = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
}

/** Why this number cannot be a card — undefined when it may be (the server still decides). */
export function giftCardNumberProblem(raw: string): string | undefined {
  const digits = digitsOf(raw);
  if (!/^\d{16}$/.test(digits) || /[^\d\s-]/.test(raw)) return "Enter the 16-digit number on the card.";
  if (!luhnValid(digits)) return "Check the card number — a digit looks wrong.";
  return undefined;
}

export function giftCardPinProblem(raw: string): string | undefined {
  return /^\d{4}$/.test(raw.trim()) ? undefined : "Enter the card’s 4-digit PIN.";
}

/** What the applied cards pay, and what the bank card is asked for (never below zero). */
export function tenderTotals(total: Money, applied: readonly AppliedGiftCard[]): { readonly byCards: Money; readonly leftToPay: Money } {
  const paid = applied.reduce((n, c) => n + c.amount.amount, 0);
  return { byCards: { amount: paid, currency: total.currency }, leftToPay: { amount: Math.max(0, total.amount - paid), currency: total.currency } };
}

/**
 * The bank card's intent key (Inv 4): an order no card touched keeps the key
 * it always had; once cards apply, the key carries the amount asked, so a
 * card applied or removed after an intent was minted asks afresh rather than
 * replaying an intent for the old figure.
 */
export function tenderIntentKey(orderId: string, applied: readonly AppliedGiftCard[], leftToPay: Money): string {
  return applied.length === 0 ? `intent-${orderId}` : `intent-${orderId}-${String(leftToPay.amount)}`;
}

const isMoney = (m: unknown): m is Money =>
  typeof m === "object" && m !== null && Number.isSafeInteger((m as Money).amount) && typeof (m as Money).currency === "string";

/**
 * B1021 leg 2 — the cards the pending order still HOLDS (`order.get`'s
 * `giftCardTenders`), as the Pay step keeps them, so a reload shows them
 * again. A drawn card belongs to a paid order (no REMOVE); a row that does
 * not read as a card is dropped, never trusted.
 */
export function appliedFromOrder(order: unknown): readonly AppliedGiftCard[] {
  const rows = typeof order === "object" && order !== null ? (order as { giftCardTenders?: unknown }).giftCardTenders : undefined;
  if (!Array.isArray(rows)) return [];
  const out: AppliedGiftCard[] = [];
  for (const row of rows as unknown[]) {
    if (typeof row !== "object" || row === null) continue;
    const r = row as Record<string, unknown>;
    if (r.drawn !== false) continue;
    if (typeof r.intentId !== "string" || r.intentId === "") continue;
    if (typeof r.lastFour !== "string" || !/^\d{4}$/.test(r.lastFour)) continue;
    if (!isMoney(r.amount) || !isMoney(r.left)) continue;
    out.push({ intentId: r.intentId, lastFour: r.lastFour, amount: { ...r.amount }, left: { ...r.left } });
  }
  return out;
}

/** The order's cards first, then any card applied on the page the read did not see — each intent once. */
export function mergeApplied(fromOrder: readonly AppliedGiftCard[], onPage: readonly AppliedGiftCard[]): readonly AppliedGiftCard[] {
  const seen = new Set(fromOrder.map((c) => c.intentId));
  return [...fromOrder, ...onPage.filter((c) => !seen.has(c.intentId))];
}

/** A gift card that PAID on a confirmed order — a receipt line. */
export interface PaidGiftCard {
  readonly lastFour: string;
  /** What the card paid on this order. */
  readonly amount: Money;
  /** What stays on the card now. */
  readonly left: Money;
}

/**
 * B1021 leg 2 — the cards that paid (`order.get`'s `giftCardTenders`, drawn)
 * for the receipts. A held card belongs to an order not yet paid; a row that
 * does not read as a card is dropped.
 */
export function paidGiftCardsFromOrder(order: unknown): readonly PaidGiftCard[] {
  const rows = typeof order === "object" && order !== null ? (order as { giftCardTenders?: unknown }).giftCardTenders : undefined;
  if (!Array.isArray(rows)) return [];
  const out: PaidGiftCard[] = [];
  for (const row of rows as unknown[]) {
    if (typeof row !== "object" || row === null) continue;
    const r = row as Record<string, unknown>;
    if (r.drawn !== true) continue;
    if (typeof r.lastFour !== "string" || !/^\d{4}$/.test(r.lastFour)) continue;
    if (!isMoney(r.amount) || !isMoney(r.left)) continue;
    out.push({ lastFour: r.lastFour, amount: { ...r.amount }, left: { ...r.left } });
  }
  return out;
}

/** "$5.00 stays on it" / "nothing left on it" — what the buyer still holds on the card. */
export function giftCardLeftWords(left: Money): string {
  return left.amount > 0 ? `${formatMoney(left)} stays on it` : "nothing left on it";
}

/** "Gift card •••• 8903 · $30.00 · $5.00 stays on it" — one receipt line. */
export function giftCardReceiptWords(card: PaidGiftCard): string {
  return `Gift card •••• ${card.lastFour} · ${formatMoney(card.amount)} · ${giftCardLeftWords(card.left)}`;
}
